import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  isFieldRequired,
  checkPatternValidation,
} from '../../../utils/fundingMethod/fundingMethodUtils';

const useForm = (initialValue, callback) => {
  const [values, setValues] = useState(initialValue);
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nasuBrkBeaconError = 'nasu-brk-beacon-error';

  /* eslint-disable */
  useEffect(() => {
    if (errors.length === 0 && isSubmitting) {
      callback();
    }
  }, [errors]);

  const errorMessages = {
    required: 'Invalid [fieldName]',
    pattern: 'Pattern does not match',
    minlength: 'Minimum [minlength] required',
    maxlength: 'Maximum [maxlength] required',
    max: 'Date cannot be greater than [maxDate]',
    min: 'Date cannot be less than [minDate]',
  };

  /**
   * Touch and blur the field to show error message if invalid
   *
   * @param {any} element
   * @param {any} fieldName
   */
  const touchField = (element, fieldName) => {
    if (element && !element.tagName.includes('RADIO')) {
      const root = element.shadowRoot.getElementById(fieldName);
      if (root) {
        element.shadowRoot.getElementById(fieldName).focus();
      }
    }
  };

  /**
   * Build error Object Pattern
   *
   * @param {any} fieldName
   * @param {any} message
   * @returns
   */
  const errorObjPattern = (fieldName, message) => {
    return {
      field: fieldName,
      message,
    };
  };

  /**
   * Validate indivisual field value for given requirements
   *
   * @param {any} fieldName
   * @param {any} errorMap
   * @param {any} fieldValue
   * @param {any} element
   * @returns
   */
  const validateField = (fieldName, errorMap, fieldValue, element) => {
    let message = '';
    if (element?.required === 'true' && !isFieldRequired(fieldValue)) {
      message = errorMap?.required
        ? errorMap?.required
        : errorMessages.required.replace('[fieldName]', fieldName);
      touchField(element, fieldName);
      return {
        field: fieldName,
        message,
      };
    }
    if (
      element.pattern &&
      !checkPatternValidation(element.pattern, fieldValue)
    ) {
      message = errorMap.pattern
        ? errorMap.pattern
        : errorMessages.pattern.replace('[fieldName]', fieldName);
      touchField(element, fieldName);
      return {
        field: fieldName,
        message,
      };
    }
    if (element.shadowRoot?.querySelector('input')?.type === 'date') {
      if (
        element.max &&
        new Date(element.max).getTime() < new Date(element.value).getTime()
      ) {
        message = errorMap.max
          ? errorMap.max
          : errorMessages.max.replace('[maxDate]', element.max);
        return {
          field: fieldName,
          message,
        };
      }
      if (
        element.min &&
        new Date(element.min).getTime() > new Date(element.value).getTime()
      ) {
        message = errorMap.min
          ? errorMap.min
          : errorMessages.min.replace('[minDate]', element.min);
        return {
          field: fieldName,
          message,
        };
      }
    }
    if (element.minlength && fieldValue.length < element.minlength) {
      message = errorMap.minlength
        ? errorMap.minlength
        : errorMessages.minlength.replace('[minlength]', element.minlength);
      return {
        field: fieldName,
        message,
      };
    }
    if (element.shadowRoot?.querySelector('input')?.type !== 'date') {
      if (element.min && fieldValue < element.min) {
        message = errorMap.min
          ? errorMap.min
          : errorMessages.minlength.replace('[minlength]', element.min);
        return {
          field: fieldName,
          message,
        };
      }
    }
    // To get the custom errors registered in the error-message-summary

    if (element.invalid) {
      message = element.shadowRoot.querySelector('#error')?.message;

      return errorObjPattern(fieldName, message);
    }
    return false;
  };

  // To validate Securities
  const validateSecurity = (key, errorList) => {
    const element =
      document.querySelector(`#${key}`) ||
      document.querySelector(`[name="${key}"]`);
    if (element) {
      const tickerValue = element.value;
      const errorMap = element.errorsMap;
      const errorObj = validateField(key, errorMap, tickerValue, element);
      element?.evaluateValidations();
      if (errorObj) {
        errorList.push(errorObj);
      }
    }
    return;
  };

  /**
   * Validate form and each field
   *
   * @param {any} formValues
   * @returns
   */
  const validateForm = (formValues) => {
    const errorList = [];

    Object.keys(formValues).forEach((key) => {
      if (key === 'onboardingTransferSecurityDetail') {
        for (var i = 0; i < 10; i++) {
          validateSecurity(`ticker_${i}`, errorList);
          validateSecurity(`shares_${i}`, errorList);
        }
      } else if (
        key === 'onboardingTransferType' &&
        formValues.onboardingTransferType !== 'UNKNOWN'
      ) {
        if (
          formValues.onboardingTransferType === 'PARTIAL' &&
          !formValues.transferCashAsset &&
          !formValues.transferSecurityAsset
        ) {
          errorList.push({
            field: 'transferCashAsset',
            message: 'Select at least one asset type.',
          });
        }
      } else {
        const fieldValue = formValues[key];
        const element =
          document.querySelector(`#${key}`) ||
          document.querySelector(`[name="${key}"]`);

        if (element) {
          const errorMap = element.errorsMap;
          const errorObj = validateField(key, errorMap, fieldValue, element);
          element?.evaluateValidations();
          if (errorObj) {
            errorList.push(errorObj);
          }
        }
      }
    });

    return errorList;
  };

  /**
   * Method to validate form on submit
   *
   * @param {any} event
   */
  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    const errorList = validateForm(values);
    setErrors(errorList);
    if (errorList.length === 0) {
      setIsSubmitting(true);
    }
  };

  /**
   * Method to call when input value is changing onBlur/onChange
   *
   * @param {any} event
   */
  const handleChange = (event) => {
    if (event.target.invalid) {
      if (errors.length > 0) {
        let isFieldFound = false;
        for (let i = 0; i < errors.length; i += 1) {
          if (errors[i].field === event.target.name) {
            isFieldFound = true;
            errors[i].message = event.target.shadowRoot.querySelector(
              nasuBrkBeaconError
            )?.message;
            if (!errors[i].message) {
              errors.splice(i, 1);
            }
          }
        }
        if (isFieldFound) {
          setErrors([...errors]);
        } else {
          setErrors([
            ...errors,
            {
              field: event.target.name,
              message: event.target.shadowRoot.querySelector(
                nasuBrkBeaconError
              )?.message,
            },
          ]);
        }
      } else {
        setErrors([
          {
            field: event.target.name,
            message: event.target.shadowRoot.querySelector(
              nasuBrkBeaconError
            )?.message,
          },
        ]);
      }
    } else {
      for (let i = 0; i < errors.length; i += 1) {
        if (errors[i].field === event.target.name) {
          errors.splice(i, 1);
          setErrors([...errors]);
          break;
        }
      }
    }

    event.persist();
    setValues(() => ({
      ...values,
      [event.target.name]: event.target.value,
    }));
  };

  return {
    handleChange,
    handleSubmit,
    values,
    errors,
    setValues,
  };
};

useForm.propTypes = {
  initialValue: PropTypes.object.required,
  callback: PropTypes.func.required,
};

export default useForm;
